// Research Deck API — drops a job message into the research-deck pipeline
// via the same AppSync endpoint as the Ad Agency / Reporter, using jobType
// "TRIGGER_RESEARCH_DECK_JOB". The AppSync resolver routes this to
// research-deck-queue → TriggerResearchDeckTask Lambda → ecs.RunTask Fargate.
import { ApolloClient, InMemoryCache, createHttpLink } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import {
  TRIGGER_AD_AGENCY_JOB_MUTATION,
  GET_LIBRARY_CARD_QUERY,
} from "../queries/libraryCardQueries";
import { getAuthToken } from "../utils/authUtils";

const GRAPHQL_ENDPOINT =
  "https://m2jxbyhagffjthxtsiuwb2iiwe.appsync-api.us-east-1.amazonaws.com/graphql";

const httpLink = createHttpLink({ uri: GRAPHQL_ENDPOINT });

const authLink = setContext((_, { headers }) => {
  const token = getAuthToken();
  return {
    headers: {
      ...headers,
      ...(token ? { Authorization: token } : {}),
    },
  };
});

const client = new ApolloClient({
  link: authLink.concat(httpLink),
  cache: new InMemoryCache(),
});

// Expected payload shape:
//   { runId, tenantId, topic, voiceMode, outputFormat?, voiceId?, slideCount?,
//     iv?, targetSeconds?,
//     knowledgeSources: ["library" | "x20_publications" | "perplexity", ...],
//     includeCitations?: boolean,
//     previousRunId?, tweakInstructions?,
//     diagram?: { repos, description, direction, lineStyle, detailLevel },
//     attachments?: [{ s3Key, s3Bucket, fileName, mimeType, size }] }
// `includeCitations`: when true the BE adds per-slide source footnotes
// (e.g. [1] perplexity.ai/…) and a trailing references slide. Citations
// only surface meaningfully when knowledgeSources includes a source that
// returns URLs (perplexity, x20_publications).
// `previousRunId` + `tweakInstructions`: do-over re-run. The BE is expected
// to load the prior run's plan/script/artifacts and apply the free-text
// tweak as a layered instruction rather than starting from scratch.
//
// ──────────────────────────────────────────────────────────────────────────
// BE handoff — formConfig round-trip:
// The FE's TicketHistoryPicker hydrates the form by reading prior tickets
// out of PROCESSEDVIDEOS (id prefix "deck_"). For hydration to work, the
// BE pipeline should persist the inbound payload onto the PROCESSEDVIDEOS
// row under either a top-level `formConfig` field or inside a
// `RESEARCHDECKTICKET.formConfig` section. The FE checks both. Persist the
// full payload (topic, voiceMode, outputFormat, slideCount, iv,
// knowledgeSources, includeCitations, diagram subfields, attachments)
// verbatim — the FE setters expect the same shape it sent.
// ──────────────────────────────────────────────────────────────────────────
// `knowledgeSources` is a non-empty subset — the BE Fargate task is expected
// to fan retrieval across each listed source and skip the rest. "library" is
// the existing DynamoDB library; "x20_publications" is the shared X20 docs
// suite; "perplexity" is live web research via the Perplexity API.
// outputFormat is "video" (default — narrated MP4), "pptx" (silent deck only,
// skips narration entirely), or "both" (narrated MP4 + silent pptx companion).
// When `diagram` is present the Fargate task runs a diagrams sub-pipeline before
// plan-slides — proposes a scope, waits for the FE to confirm or refine, then
// renders one diagram and folds it into the deck as a diagram-hero slide.
// `attachments` are creator-uploaded images (snapshots of notes, screenshots)
// that the BE Fargate task is expected to fetch from S3 and feed to a vision
// model so the topic text can reference them ("see snap").
export const triggerResearchDeckApi = async (payload) => {
  const { data } = await client.mutate({
    mutation: TRIGGER_AD_AGENCY_JOB_MUTATION,
    variables: {
      input: {
        jobType: "TRIGGER_RESEARCH_DECK_JOB",
        tenantId: payload.tenantId,
        payload: JSON.stringify(payload),
      },
    },
  });
  return data.triggerAdAgencyJob;
};

// Confirm the diagrams-agent's proposed scope for a deck run that's paused at
// phase=awaiting_diagram_confirm. Patches RESEARCHDECKTICKET so the Fargate
// task's wait-loop picks up the confirmation on its next poll and kicks off
// the diagrams render.
export const triggerDeckConfirmDiagramScopeApi = async ({
  runId,
  tenantId,
  confirmedScope,
  repoRefs,
}) => {
  const { data } = await client.mutate({
    mutation: TRIGGER_AD_AGENCY_JOB_MUTATION,
    variables: {
      input: {
        jobType: "TRIGGER_DECK_CONFIRM_DIAGRAM_SCOPE",
        tenantId,
        payload: JSON.stringify({ runId, confirmedScope, repoRefs }),
      },
    },
  });
  return data.triggerAdAgencyJob;
};

// Ask the deck to re-interpret the diagram with a new description. Capped at
// 3 refines per deckRunId by the FE; the BE additionally hard-caps in the
// resolver so a stale tab can't blow through the limit.
export const triggerDeckRefineDiagramApi = async ({
  runId,
  tenantId,
  newDescription,
}) => {
  const { data } = await client.mutate({
    mutation: TRIGGER_AD_AGENCY_JOB_MUTATION,
    variables: {
      input: {
        jobType: "TRIGGER_DECK_REFINE_DIAGRAM",
        tenantId,
        payload: JSON.stringify({ runId, newDescription }),
      },
    },
  });
  return data.triggerAdAgencyJob;
};

// Fetch a RESEARCHDECKTICKET row via the shared library-card resolver.
// The BE writes all ticket fields inside a JSON-stringified `data` field
// (library-card convention), so we parse it before handing back to the UI.
// Returns null if the row hasn't been written yet (happens briefly after
// submit, before TriggerResearchDeckTask Lambda drains the queue).
export const getResearchDeckTicketApi = async ({ tenantId, runId }) => {
  try {
    const { data } = await client.query({
      query: GET_LIBRARY_CARD_QUERY,
      variables: {
        input: {
          operationType: "GET_LIBRARY_CARD",
          tenantId,
          entityType: "RESEARCHDECKTICKET",
          entityId: runId,
        },
      },
      fetchPolicy: "no-cache",
    });
    const card = data?.getLibraryCard;
    if (!card || !card.data) return null;
    // The resolver returns `data` as an already-parsed object for some
    // entity types and a JSON string for others — handle both.
    return typeof card.data === "string" ? JSON.parse(card.data) : card.data;
  } catch (err) {
    // Not-found is the normal pre-QUEUED case; treat as "still pending".
    const msg = String(err?.message || "");
    if (msg.includes("not found")) return null;
    throw err;
  }
};
